import { useInvoiceReportsSearch } from '@/context/services/invoice_reports/InvoiceReportSearch.service';

import {
  FetchInvoiceReportsArgs,
  InvoiceReportsSearchQuery,
} from '@/features/invoice_reports/types';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  ModernPaginationProps,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect, useState } from 'react';

export const useInvoiceReportsSearchData = (
  query: FetchInvoiceReportsArgs,
  setError: (error: Error) => void
) => {
  const {
    data,
    pagination: _pagination,
    refetch,
    isFetching,
    isError,
    error,
  } = useInvoiceReportsSearch(query);

  const [isInitialError, setIsInitialError] = useState(true);
  const notify = useToastAPI();
  const TOAST_NOTICE_MESSAGE_KEY = 'toast-notice-message';

  const handleError = useCallback(() => {
    const errorMessage =
      isCommonError(error ?? {}) && Array.isArray(error?.messages)
        ? error?.messages.join('')
        : String(error?.messages);
    if (errorMessage !== null && errorMessage !== 'undefined') {
      setError({
        name: 'InvoiceReportsTableError',
        message: String(errorMessage),
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isInitialError && isError && !isFetching) {
      setIsInitialError(false);
      handleError();
      return;
    }
    if (isError && !isFetching) {
      handleError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isInitialError]);

  useEffect(() => {
    const toastNoticeMessage = sessionStorage.getItem(TOAST_NOTICE_MESSAGE_KEY);
    if (toastNoticeMessage) {
      notify.success({
        title: toastNoticeMessage,
        duration: 3,
      });
      sessionStorage.removeItem(TOAST_NOTICE_MESSAGE_KEY);
    }
  }, [notify]);

  return {
    data,
    pagination: _pagination,
    refetch,
    isFetching,
  };
};

type useReportActionsProps = (props: {
  pagination: ModernPaginationProps;
  onSearch: (value: InvoiceReportsSearchQuery) => void;
  onDrawerClose: () => void;
}) => {
  onActionFailed: () => void;
};

export const useReportActions: useReportActionsProps = ({
  pagination,
  onSearch,
  onDrawerClose,
}) => {
  const onActionFailed = useCallback(() => {
    if (pagination.currentPage !== 1) {
      onSearch({ page: 1 });
      return;
    }
    onDrawerClose();
    onSearch({ page: pagination.currentPage });
  }, [pagination.currentPage, onSearch, onDrawerClose]);

  return {
    onActionFailed,
  };
};
